import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FaEdit, FaFileExport, FaInbox, FaPlus, FaSearch, FaTimes, FaTrash } from 'react-icons/fa';
import { API } from 'aws-amplify';
import { useToasts } from 'react-toast-notifications';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Pagination from '../../../components/Pagination/Pagination';
import { ITEMS_PER_PAGE } from '../../../localdb';
import '../../../components/PageHeader/PageHeader.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useI18n } from '../../../i18n';
import './Inventory.css';

const Inventory = (props) => {
  const { t } = useI18n();
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportReport, setShowImportReport] = useState(false);
  const [importReport, setImportReport] = useState({
    totalRows: 0,
    imported: 0,
    skipped: 0,
    overwritten: 0,
    duplicatedInFile: 0,
    details: [],
  });
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editItemId, setEditItemId] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');

  const params = {
    headers: {
      'x-api-key': process.env.REACT_APP_API_KEY,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const itemsPerPage = ITEMS_PER_PAGE;

  const normalizeItemId = (raw) => {
    let value = raw === undefined || raw === null ? '' : String(raw).trim();
    const excelTextMatch = value.match(/^=\s*"(.+)"$/);
    if (excelTextMatch && excelTextMatch[1] !== undefined) {
      value = excelTextMatch[1];
    }
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.substring(1, value.length - 1).trim();
    }
    return value;
  };

  const sortByItemId = (list) => {
    return [...list].sort((a, b) => String(a.itemId || '').localeCompare(String(b.itemId || '')));
  };

  const getErrorMessage = (error) => {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (error.response && error.response.data) {
      if (typeof error.response.data === 'string') return error.response.data;
      try {
        return JSON.stringify(error.response.data);
      } catch (e) {
        return 'API error response could not be displayed';
      }
    }
    if (error.message) return String(error.message);
    try {
      return JSON.stringify(error);
    } catch (e) {
      return 'Unknown error';
    }
  };

  const safeText = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '-';
    }
  };

  // Accepts: "1,200.00", "1.200,00", "€ 12.50", " 12,50 ", "12.5", etc.
  // Returns a Number, or NaN if it cannot be parsed.
  const parseRate = (raw) => {
    if (raw === undefined || raw === null) return NaN;
    let value = String(raw).trim();
    if (value === '') return NaN;
    value = value.replace(/[€$£\s]/g, '');
    const hasComma = value.indexOf(',') !== -1;
    const hasDot = value.indexOf('.') !== -1;
    if (hasComma && hasDot) {
      if (value.lastIndexOf(',') > value.lastIndexOf('.')) {
        value = value.replace(/\./g, '').replace(',', '.');
      } else {
        value = value.replace(/,/g, '');
      }
    } else if (hasComma) {
      const parts = value.split(',');
      const last = parts[parts.length - 1];
      if (parts.length === 2 && last.length !== 3) {
        value = value.replace(',', '.');
      } else {
        value = value.replace(/,/g, '');
      }
    }
    const n = Number(value);
    return Number.isNaN(n) ? NaN : n;
  };

  const loadInventory = async () => {
    setIsLoading(true);
    try {
      const res = await API.get('inventory', '/', params);
      setItems(Array.isArray(res) ? sortByItemId(res) : []);
    } catch (error) {
      console.log(error);
      addToast(t('toast.errorLoadingInventory'), { appearance: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      addToast(t('toast.descriptionRequired'), { appearance: 'warning' });
      return;
    }
    const p = price === '' || price === null || price === undefined ? 0 : parseRate(price);
    if (Number.isNaN(p)) {
      addToast(t('toast.rateMustBeNumber'), { appearance: 'warning' });
      return;
    }

    try {
      await API.post('inventory', '/', {
        body: {
          itemId: normalizeItemId(itemId) || undefined,
          description: description.trim(),
          price: p,
          updated_user: props.user.username,
        },
        headers: params.headers,
      });
      addToast(t('toast.inventoryAdded'), { appearance: 'success' });
      setItemId('');
      setDescription('');
      setPrice('');
      await loadInventory();
    } catch (error) {
      console.log(error);
      addToast(t('toast.errorAddingInventory'), { appearance: 'error' });
    }
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setEditItemId(item.itemId || '');
    setEditDescription(item.description || '');
    setEditPrice(item.price === undefined || item.price === null ? '' : String(item.price));
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!selectedItem) return;
    if (!editDescription.trim()) {
      addToast(t('toast.descriptionRequired'), { appearance: 'warning' });
      return;
    }
    const p = editPrice === '' || editPrice === null || editPrice === undefined ? 0 : parseRate(editPrice);
    if (Number.isNaN(p)) {
      addToast(t('toast.rateMustBeNumber'), { appearance: 'warning' });
      return;
    }

    try {
      const encodedId = encodeURIComponent(String(selectedItem.itemId || ''));
      await API.put('inventory', '/' + encodedId, {
        body: {
          itemId: normalizeItemId(editItemId),
          description: editDescription.trim(),
          price: p,
          updated_user: props.user.username,
        },
        headers: params.headers,
      });
      addToast(t('toast.inventoryUpdated'), { appearance: 'success' });
      setShowEdit(false);
      await loadInventory();
    } catch (error) {
      console.log(error);
      addToast(t('toast.errorUpdatingInventory'), { appearance: 'error' });
    }
  };

  const openDelete = (item) => {
    setSelectedItem(item);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      const encodedId = encodeURIComponent(String(selectedItem.itemId || ''));
      await API.del('inventory', '/' + encodedId, {
        body: {
          itemId: normalizeItemId(selectedItem.itemId || ''),
        },
        headers: params.headers,
      });
      addToast(t('toast.inventoryDeleted'), { appearance: 'success' });
      setShowDelete(false);
      await loadInventory();
    } catch (error) {
      console.log(error);
      addToast(t('toast.errorDeletingInventory', { id: selectedItem.itemId || '-' }), { appearance: 'error' });
    }
  };

  const exportCsv = () => {
    const header = ['Item ID', 'Description', 'Rate'];
    const rows = items.map((item) => {
      const p = Number(item.price || 0);
      const itemIdText = String(item.itemId || '').replace(/"/g, '""');
      return [
        `="${itemIdText}"`,
        item.description || '',
        p,
      ];
    });
    const allRows = [header, ...rows];
    const csv = allRows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'inventory.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const parseCsvLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((v) => v.trim());
  };

  const importCsv = async (event) => {
    const inputEl = event.target;
    const file = inputEl.files && inputEl.files[0];
    if (!file) return;

    setIsImporting(true);
    setShowImportReport(false);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
      if (lines.length <= 1) {
        setImportReport({
          totalRows: 0,
          imported: 0,
          skipped: 0,
          overwritten: 0,
          duplicatedInFile: 0,
          details: [{ row: '-', itemId: '-', description: '-', reason: 'CSV has no data rows' }],
        });
        setShowImportReport(true);
        return;
      }
      const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
      const itemIdIdx = headers.findIndex((h) => h.includes('item id') || h === 'itemid' || h === 'item_id');
      const descIdx = headers.findIndex((h) => h.includes('description'));
      const priceIdx = headers.findIndex((h) => h.includes('rate') || h.includes('price'));
      if (descIdx < 0 || priceIdx < 0) {
        setImportReport({
          totalRows: lines.length - 1,
          imported: 0,
          skipped: lines.length - 1,
          overwritten: 0,
          duplicatedInFile: 0,
          details: [{ row: '-', itemId: '-', description: '-', reason: 'CSV must include Description and Rate columns' }],
        });
        setShowImportReport(true);
        return;
      }

      let imported = 0;
      let duplicatedInFile = 0;
      let existingOverwrites = 0;
      let skipped = 0;
      const details = [];
      const seenItemIds = new Set();
      const existingItemIds = new Set(items.map((item) => normalizeItemId(item.itemId || '')));
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        const rowNumber = i + 1;
        const desc = (cols[descIdx] || '').trim();
        const normalizedId = itemIdIdx >= 0 ? normalizeItemId(cols[itemIdIdx] || '') : '';
        if (!desc) {
          skipped++;
          details.push({
            row: rowNumber,
            itemId: normalizedId || '-',
            description: '-',
            reason: 'Skipped: missing description',
          });
          continue;
        }
        const rawRate = cols[priceIdx] || '';
        const p = rawRate === '' ? 0 : parseRate(rawRate);
        if (Number.isNaN(p)) {
          skipped++;
          details.push({
            row: rowNumber,
            itemId: normalizedId || '-',
            description: desc,
            reason: `Skipped: invalid rate "${rawRate}"`,
          });
          continue;
        }
        if (normalizedId) {
          if (seenItemIds.has(normalizedId)) {
            duplicatedInFile++;
          }
          if (existingItemIds.has(normalizedId)) {
            existingOverwrites++;
          }
          seenItemIds.add(normalizedId);
        }

        try {
          await API.post('inventory', '/', {
            body: {
              itemId: normalizedId || undefined,
              description: desc,
              price: p,
              updated_user: props.user.username,
            },
            headers: params.headers,
          });
          imported++;
        } catch (rowError) {
          skipped++;
          const message = getErrorMessage(rowError);
          details.push({
            row: rowNumber,
            itemId: normalizedId || '-',
            description: desc,
            reason: `Skipped: API error - ${message}`,
          });
        }
      }

      setImportReport({
        totalRows: lines.length - 1,
        imported,
        skipped,
        overwritten: existingOverwrites,
        duplicatedInFile,
        details,
      });
      setShowImportReport(true);
      addToast(t('toast.importFinished', { n: imported }), { appearance: 'success' });
      loadInventory();
    } catch (error) {
      console.log(error);
      setImportReport({
        totalRows: 0,
        imported: 0,
        skipped: 0,
        overwritten: 0,
        duplicatedInFile: 0,
        details: [{
          row: '-',
          itemId: '-',
          description: '-',
          reason: getErrorMessage(error),
        }],
      });
      setShowImportReport(true);
      addToast(t('toast.errorImportingCsv'), { appearance: 'error' });
    } finally {
      setIsImporting(false);
      if (inputEl) {
        try { inputEl.value = ''; } catch (e) { /* ignore */ }
      }
    }
  };

  const filteredItems = sortByItemId(items).filter((item) => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    return String(item.itemId || '').toLowerCase().includes(q) ||
      String(item.description || '').toLowerCase().includes(q);
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItemList = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const safeImportDetails = Array.isArray(importReport.details) ? importReport.details : [];

  return (
    <div className="Inventory">
      <PageHeader
        title={t('pages.inventory.title')}
        subtitle={t('pages.inventory.subtitle')}
        breadcrumbs={[
          { label: t('nav.costs') },
          { label: t('pages.inventory.title') },
        ]}
        count={!isLoading ? items.length : undefined}
        countLabel={t('common.items')}
        countSingular={t('common.item')}
        countLoading={isLoading}
      />

      <div className="row div-content">
        <div className="col-md-8">
          <div className="add-panel">
            <div className="section-title">
              <FaPlus /> {t('costsUi.addItem')}
            </div>
            <Form onSubmit={handleAdd}>
              <div className="row">
                <Form.Group className="col-md-3">
                  <Form.Label>{t('costsUi.itemIdOptional')}</Form.Label>
                  <Form.Control
                    value={itemId}
                    placeholder={t('costsUi.autoId')}
                    onChange={(e) => setItemId(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-4">
                  <Form.Label>{t('common.description')}</Form.Label>
                  <Form.Control
                    value={description}
                    placeholder={t('costsUi.itemDescription')}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-2">
                  <Form.Label>{t('costsUi.rateEuro')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={price}
                    placeholder="0.00"
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </Form.Group>
                <div className="col-md-3 add-btn-wrap">
                  <Button type="submit" variant="success" className="btn-add">
                    <FaPlus /> {t('costsUi.addItem')}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>

        <div className="col-md-4">
          <div className="inventory-summary-box">
            <h6>{t('costsUi.materialsList')}</h6>
            <div className="summary-count">{items.length}</div>
            <div className="d-flex inventory-actions">
              <Button size="sm" variant="warning" onClick={exportCsv}>
                <FaFileExport /> {t('common.export')}
              </Button>
              <Form.File
                id="inventory-import"
                custom
                label={isImporting ? t('common.importing') : t('common.importCsv')}
                data-browse={t('common.browse')}
                accept=".csv"
                onChange={importCsv}
                disabled={isImporting}
              />
            </div>
            {isImporting ? (
              <div className="import-status">
                <Spinner size="sm" animation="border" role="status" variant="warning" />
                <span>{t('costsUi.importingFile')}</span>
              </div>
            ) : null}
            <small className="text-muted d-block mt-2">
              {t('costsUi.csvColumns')}
            </small>
            <small className="text-muted d-block">
              {t('costsUi.rateHint')}
            </small>
          </div>
        </div>

        <div className="col-md-12 mt-3">
          <div className="search-bar">
            <div className="search-input-wrap">
              <FaSearch className="search-icon" />
              <Form.Control
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t('costsUi.searchItems')}
              />
            </div>
            {searchText.trim() ? (
              <button
                type="button"
                className="search-clear"
                onClick={() => {
                  setSearchText('');
                  setCurrentPage(1);
                }}
              >
                {t('costsUi.clearSearch')}
              </button>
            ) : null}
            <span className="search-meta">
              {t('costsUi.showingSorted', { shown: filteredItems.length, total: items.length })}
            </span>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-content">
                <Spinner animation="border" role="status" variant="primary" className="loading-spinner" />
                <div className="loading-text">{t('costsUi.loadingInv')}</div>
              </div>
            </div>
          ) : currentItemList.length === 0 ? (
            <div className="empty-state">
              <FaInbox className="empty-state-icon" />
              <div className="empty-state-text">{t('costsUi.noItems')}</div>
              <div className="empty-state-subtext">
                {searchText.trim() ? t('costsUi.trySearch') : t('costsUi.addOrImport')}
              </div>
            </div>
          ) : (
            <Table responsive striped bordered hover variant="light" className="inventory-table">
              <thead>
                <tr>
                  <th>{t('fields.itemId')}</th>
                  <th className="inventory-description-col">{t('common.description')}</th>
                  <th className="text-right">{t('costsUi.rateEuro')}</th>
                  <th>{t('costsUi.updatedAt')}</th>
                  <th className="text-right">{t('costsUi.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {currentItemList.map((item) => (
                  <tr key={item.itemId}>
                    <td>{item.itemId || '-'}</td>
                    <td className="inventory-description-col">{item.description}</td>
                    <td className="text-right">{Number(item.price || 0).toFixed(2)}</td>
                    <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                    <td className="text-right">
                      <Button size="sm" variant="primary" className="action-btn" onClick={() => openEdit(item)} title="Edit">
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="danger" className="action-btn" onClick={() => openDelete(item)} title="Delete">
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {!isLoading && filteredItems.length > 0 ? (
            <Pagination
              sizePerPage={itemsPerPage}
              currentPage={currentPage}
              totalSize={filteredItems.length}
              changeCurrentPage={setCurrentPage}
              theme="bootstrap"
            />
          ) : null}
        </div>
      </div>

      <Modal className="inventory-modal" show={showEdit} onHide={() => setShowEdit(false)} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('costsUi.editItem')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('fields.itemId')}</Form.Label>
            <Form.Control value={editItemId} onChange={(e) => setEditItemId(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('common.description')}</Form.Label>
            <Form.Control value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>{t('costsUi.rateEuro')}</Form.Label>
            <Form.Control type="number" step="0.01" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleUpdate}>{t('common.save')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal className="inventory-modal inventory-modal-danger" show={showDelete} onHide={() => setShowDelete(false)} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('costsUi.deleteItem')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('costsUi.deleteConfirmItem')} <strong>{selectedItem ? selectedItem.description : ''}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDelete}>{t('actions.delete')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        className="inventory-modal"
        show={showImportReport}
        onHide={() => setShowImportReport(false)}
        size="lg"
        centered
        backdrop={true}
        keyboard={true}
        enforceFocus={false}
        restoreFocus={false}
        autoFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('costsUi.importReport')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="import-stats">
            <div className="import-stat">{t('costsUi.totalRows')}<strong>{safeText(importReport.totalRows)}</strong></div>
            <div className="import-stat">{t('costsUi.processed')}<strong>{safeText(importReport.imported)}</strong></div>
            <div className="import-stat">{t('costsUi.skipped')}<strong>{safeText(importReport.skipped)}</strong></div>
            <div className="import-stat">{t('costsUi.overwritten')}<strong>{safeText(importReport.overwritten)}</strong></div>
            <div className="import-stat">{t('costsUi.duplicates')}<strong>{safeText(importReport.duplicatedInFile)}</strong></div>
          </div>
          {safeImportDetails.length > 0 ? (
            <React.Fragment>
              <div className="text-danger mb-2">
                {t('costsUi.rowsFailed')}
              </div>
              <div className="inventory-import-report">
                <Table responsive striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>{t('costsUi.row')}</th>
                      <th>{t('fields.itemId')}</th>
                      <th>{t('common.description')}</th>
                      <th>{t('costsUi.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeImportDetails.map((detail, idx) => (
                      <tr key={idx}>
                        <td>{safeText(detail && detail.row)}</td>
                        <td>{safeText(detail && detail.itemId)}</td>
                        <td>{safeText(detail && detail.description)}</td>
                        <td>{safeText(detail && detail.reason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </React.Fragment>
          ) : (
            <div className="text-success">{t('costsUi.allProcessed')}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportReport(false)}>{t('common.close')}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(Inventory);
